import { Briefcase, Shield, Wallet } from "lucide-react";
import { type ReactNode } from "react";

interface StepCardProps {
  stepNumber: string;
  icon: ReactNode;
  title: string;
  description: string;
  align: "left" | "right";
}

function StepCard({ stepNumber, icon, title, description, align }: StepCardProps) {
  return (
    <div
      className={`relative ${
        align === "right" ? "ml-auto" : ""
      } max-w-lg w-full`}
    >
      {/* Large overlapping step number */}
      <span className="font-display text-7xl sm:text-8xl font-bold text-accent-cyan/20 absolute -top-8 -left-4 select-none pointer-events-none leading-none">
        {stepNumber}
      </span>

      {/* Card */}
      <div className="card-organic p-6 sm:p-8 relative group hover:bg-surface-hover transition-all duration-300">
        {/* Icon */}
        <div className="w-12 h-12 rounded-xl bg-bg-secondary border border-surface-border flex items-center justify-center mb-5 group-hover:glow-cyan transition-all duration-300">
          <div className="text-accent-cyan">{icon}</div>
        </div>

        {/* Content */}
        <h3 className="font-display text-xl font-semibold text-white mb-3">
          {title}
        </h3>
        <p className="text-gray-400 leading-relaxed text-sm sm:text-base">
          {description}
        </p>
      </div>
    </div>
  );
}

const steps: Omit<StepCardProps, "align">[] = [
  {
    stepNumber: "01",
    icon: <Briefcase size={28} />,
    title: "Post or Find",
    description:
      "Agencies post jobs with crypto-backed escrow. Workers browse verified opportunities and apply anonymously. No real names required.",
  },
  {
    stepNumber: "02",
    icon: <Shield size={28} />,
    title: "Secure Escrow",
    description:
      "Funds are locked in smart-contract escrow before work begins. Both parties are protected. Disputes are resolved fairly and transparently.",
  },
  {
    stepNumber: "03",
    icon: <Wallet size={28} />,
    title: "Get Paid in Crypto",
    description:
      "When work is approved, payment is released instantly to the worker's wallet. No banks, no delays, no chargebacks. Pure crypto settlement.",
  },
];

const alignments: ("left" | "right")[] = ["left", "right", "left"];

export default function HowItWorks() {
  return (
    <section id="how-it-works" className="section-padding relative">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-glow opacity-30 pointer-events-none" />

      <div className="section-container relative">
        {/* Header — left-aligned with cyan accent line */}
        <div className="accent-line-cyan mb-16 max-w-2xl">
          <span className="text-accent-cyan uppercase tracking-widest text-xs font-mono mb-3 block">
            How It Works
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Three steps to{" "}
            <span className="text-gradient-cyan">secure hiring</span>
          </h2>
          <p className="text-lg text-gray-400 leading-relaxed">
            From posting a job to getting paid, everything is encrypted,
            escrowed, and anonymous.
          </p>
        </div>

        {/* Vertical timeline */}
        <div className="relative">
          {/* Connecting vertical dotted line (desktop) */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px border-l-2 border-dashed border-accent-cyan/20" />

          {/* Steps */}
          <div className="space-y-16 sm:space-y-20 md:space-y-24">
            {steps.map((step, index) => (
              <StepCard
                key={step.title}
                {...step}
                align={alignments[index] ?? "left"}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
